import { safeParsePhone, toDisplayFormat, toWhatsappFormat } from '@/lib/phone';

export interface SupportContact {
  id: string;
  label: string;
  phone: string;
  whatsappPhone?: string;
  isDefault: boolean;
}

export interface SupportContactLegacyFields {
  supportPhone?: string | null;
  whatsappBusinessNumber?: string | null;
}

function createId(seed: number): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `support-contact-${seed}-${Date.now()}`;
}

function normalizeContact(
  contact: Partial<SupportContact>,
  index: number
): SupportContact | null {
  const normalizedPhone = safeParsePhone(contact.phone || contact.whatsappPhone || '');
  if (!normalizedPhone) {
    return null;
  }

  const normalizedWhatsApp = safeParsePhone(contact.whatsappPhone || '') || undefined;

  return {
    id: contact.id || createId(index),
    label: contact.label?.trim() || `Support ${index + 1}`,
    phone: normalizedPhone,
    whatsappPhone: normalizedWhatsApp,
    isDefault: Boolean(contact.isDefault),
  };
}

export function ensureSupportContactDefaults(
  contacts: SupportContact[]
): SupportContact[] {
  const normalizedContacts = contacts.filter(Boolean);
  if (normalizedContacts.length === 0) {
    return [];
  }

  let defaultIndex = normalizedContacts.findIndex((contact) => contact.isDefault);
  if (defaultIndex < 0) {
    defaultIndex = 0;
  }

  return normalizedContacts.map((contact, index) => ({
    ...contact,
    isDefault: index === defaultIndex,
  }));
}

export function parseSupportContacts(
  rawValue?: string | null,
  legacyFields: SupportContactLegacyFields = {}
): SupportContact[] {
  const parsedContacts: SupportContact[] = [];

  if (rawValue) {
    try {
      const candidate = JSON.parse(rawValue);
      if (Array.isArray(candidate)) {
        candidate.forEach((value, index) => {
          const normalized = normalizeContact(value as Partial<SupportContact>, index);
          if (normalized) {
            parsedContacts.push(normalized);
          }
        });
      }
    } catch {
      // Ignore invalid JSON and fall back to legacy values.
    }
  }

  if (parsedContacts.length > 0) {
    return ensureSupportContactDefaults(parsedContacts);
  }

  const legacyPhone = safeParsePhone(legacyFields.supportPhone || '');
  const legacyWhatsApp = safeParsePhone(legacyFields.whatsappBusinessNumber || '');
  const fallbackPhone = legacyPhone || legacyWhatsApp;

  if (!fallbackPhone) {
    return [];
  }

  return [
    {
      id: 'legacy-support-contact',
      label: 'Support',
      phone: fallbackPhone,
      whatsappPhone: legacyWhatsApp || undefined,
      isDefault: true,
    },
  ];
}

export function serializeSupportContacts(contacts: SupportContact[]): string {
  const normalizedContacts = ensureSupportContactDefaults(
    contacts
      .map((contact, index) => normalizeContact(contact, index))
      .filter((contact): contact is SupportContact => Boolean(contact))
  );

  return JSON.stringify(normalizedContacts);
}

export function getDefaultSupportContact(
  contacts: SupportContact[]
): SupportContact | null {
  const normalizedContacts = ensureSupportContactDefaults(contacts);
  return normalizedContacts.find((contact) => contact.isDefault) || normalizedContacts[0] || null;
}

export function getContactWhatsappTarget(contact: SupportContact | null): string {
  if (!contact) {
    return '';
  }

  const result = toWhatsappFormat(contact.whatsappPhone || contact.phone);
  return result.success ? result.data : '';
}

export function getContactDisplayNumber(contact: SupportContact | null): string {
  if (!contact) {
    return '';
  }

  return toDisplayFormat(contact.phone);
}

export function createEmptySupportContact(seed: number): SupportContact {
  return {
    id: createId(seed),
    label: `Support ${seed + 1}`,
    phone: '',
    whatsappPhone: '',
    isDefault: seed === 0,
  };
}
